import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  RefreshCw,
  Save,
  Wallet,
} from 'lucide-react';
import {
  getCreditOrder,
  getTeamCreditOrder,
  saveCreditOrder,
  saveTeamCreditOrder,
} from './creditOrderApi';

function formatExpiry(value, english) {
  if (Number(value) === -1) return english ? 'Never expires' : '永久有效';
  return new Date(Number(value) * 1000).toLocaleDateString(
    english ? 'en-US' : 'zh-CN',
    { year: 'numeric', month: '2-digit', day: '2-digit' },
  );
}

function moveItem(items, fromIndex, toIndex) {
  const next = [...items];
  if (toIndex < 0 || toIndex >= next.length || fromIndex < 0 || fromIndex >= next.length) return next;
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export default function CreditOrderPanel({
  userId,
  preview = false,
  english = false,
}) {
  const [activeTab, setActiveTab] = useState('personal');
  const [data, setData] = useState(null);
  const [order, setOrder] = useState([]);
  const [priority, setPriority] = useState('personal');
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [dragIndex, setDragIndex] = useState(null);

  const [teamData, setTeamData] = useState(null);
  const [teamOrder, setTeamOrder] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamFeedback, setTeamFeedback] = useState({ type: '', message: '' });
  const [teamDragIndex, setTeamDragIndex] = useState(null);
  const aliveRef = useRef(true);

  const applyPersonalData = (next) => {
    setData(next);
    setPriority(next?.wallet_priority || 'personal');
    setOrder(Array.isArray(next?.order) ? next.order : []);
  };

  const applyTeamData = (next) => {
    setTeamData(next);
    setTeamOrder(Array.isArray(next?.order) ? next.order : []);
  };

  const loadTeam = async () => {
    if (preview || !userId) return;
    setTeamLoading(true);
    setTeamError('');
    try {
      const next = await getTeamCreditOrder();
      if (aliveRef.current) applyTeamData(next);
    } catch (requestError) {
      if (aliveRef.current) setTeamError(getErrorMessage(requestError, '团队积分顺序暂不可用'));
    } finally {
      if (aliveRef.current) setTeamLoading(false);
    }
  };

  const loadPersonal = async () => {
    if (preview || !userId) return;
    setLoading(true);
    setError('');
    try {
      const next = await getCreditOrder();
      if (aliveRef.current) {
        applyPersonalData(next);
        if (next?.team_can_manage && next?.team) {
          loadTeam();
        }
      }
    } catch (requestError) {
      if (aliveRef.current) setError(getErrorMessage(requestError, '积分消耗顺序暂不可用'));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    aliveRef.current = true;
    loadPersonal();
    return () => { aliveRef.current = false; };
  }, [preview, userId]);

  const teamAvailable = Number(data?.team_available_credits || 0) > 0 && Boolean(data?.team);
  const teamCanManage = Boolean(data?.team_can_manage && data?.team);

  const personalSourceMap = useMemo(() => new Map(
    (data?.sources || []).map(source => [source.key, source]),
  ), [data?.sources]);
  const orderedPersonalSources = useMemo(
    () => order.map(key => personalSourceMap.get(key)).filter(Boolean),
    [order, personalSourceMap],
  );

  const teamSourceMap = useMemo(() => new Map(
    (teamData?.sources || []).map(source => [source.key, source]),
  ), [teamData?.sources]);
  const orderedTeamSources = useMemo(
    () => teamOrder.map(key => teamSourceMap.get(key)).filter(Boolean),
    [teamOrder, teamSourceMap],
  );

  const persistPersonal = async (nextPriority, nextOrder) => {
    if (preview || saving || !userId) return;
    const previousPriority = priority;
    const previousOrder = order;
    setPriority(nextPriority);
    setOrder(nextOrder);
    setSaving(true);
    setFeedback({ type: '', message: '' });
    try {
      const next = await saveCreditOrder({
        wallet_priority: nextPriority,
        sources: nextOrder,
      });
      if (aliveRef.current) {
        applyPersonalData(next);
        setFeedback({ type: 'success', message: english ? 'Saved' : '已保存' });
      }
    } catch (saveError) {
      if (!aliveRef.current) return;
      setPriority(previousPriority);
      setOrder(previousOrder);
      setFeedback({
        type: 'error',
        message: getErrorMessage(saveError, '保存失败'),
      });
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const persistTeam = async (nextOrder) => {
    if (preview || teamSaving || !userId) return;
    const previousOrder = teamOrder;
    setTeamOrder(nextOrder);
    setTeamSaving(true);
    setTeamFeedback({ type: '', message: '' });
    try {
      const next = await saveTeamCreditOrder({ sources: nextOrder });
      if (aliveRef.current) {
        applyTeamData(next);
        setTeamFeedback({ type: 'success', message: english ? 'Saved' : '已保存' });
      }
    } catch (saveError) {
      if (!aliveRef.current) return;
      setTeamOrder(previousOrder);
      setTeamFeedback({
        type: 'error',
        message: getErrorMessage(saveError, '保存失败'),
      });
    } finally {
      if (aliveRef.current) setTeamSaving(false);
    }
  };

  const changePriority = (value) => {
    if (value === 'team' && !teamAvailable) return;
    if (value !== priority) persistPersonal(value, order);
  };

  const reorderPersonal = (fromIndex, toIndex) => {
    const nextOrder = moveItem(order, fromIndex, toIndex);
    if (nextOrder !== order) persistPersonal(priority, nextOrder);
  };

  const reorderTeam = (fromIndex, toIndex) => {
    const nextOrder = moveItem(teamOrder, fromIndex, toIndex);
    if (nextOrder !== teamOrder) persistTeam(nextOrder);
  };

  const handlePersonalDrop = (targetIndex) => {
    if (dragIndex === null) return;
    reorderPersonal(dragIndex, targetIndex);
    setDragIndex(null);
  };

  const handleTeamDrop = (targetIndex) => {
    if (teamDragIndex === null) return;
    reorderTeam(teamDragIndex, targetIndex);
    setTeamDragIndex(null);
  };

  const renderWalletCards = () => (
    <div className="credit-order-panel__wallets">
      <div className="credit-order-panel__wallet">
        <Wallet size={16} />
        <div>
          <strong>{english ? 'Personal credits' : '个人积分'}</strong>
          <span>{Number(data?.personal_available_credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</span>
        </div>
      </div>
      <div className="credit-order-panel__wallet">
        <Wallet size={16} />
        <div>
          <strong>{english ? 'Current team credits' : '当前团队积分'}</strong>
          <span>{data?.team ? `${data.team.team_name} · ${Number(data.team.credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')}` : (english ? 'No current team' : '暂无当前团队')}</span>
        </div>
      </div>
    </div>
  );

  const renderPersonalTab = () => (
    <>
      <div className="credit-order-panel__priority" role="radiogroup" aria-label={english ? 'Preferred wallet' : '优先消耗钱包'}>
        <button
          type="button"
          role="radio"
          aria-checked={priority === 'personal'}
          className={priority === 'personal' ? 'is-active' : ''}
          onClick={() => changePriority('personal')}
          disabled={preview}
        >
          {english ? 'Personal first' : '优先个人'}
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={priority === 'team'}
          className={priority === 'team' ? 'is-active' : ''}
          onClick={() => changePriority('team')}
          disabled={preview || !teamAvailable}
        >
          {english ? 'Team first' : '优先团队'}
        </button>
      </div>

      <div className="credit-order-panel__notice">
        {english
          ? 'A single charge uses only one wallet. If the preferred wallet is insufficient, the other wallet is tried as a whole.'
          : '单次消费只扣一个钱包；优先钱包不足时，会整笔尝试另一个钱包。'}
      </div>

      {orderedPersonalSources.length === 0 ? (
        <div className="credit-order-panel__state">{english ? 'No personal credit batches available.' : '暂无可排序的个人积分批次。'}</div>
      ) : (
        <div className="credit-order-panel__list">
          {orderedPersonalSources.map((source, index) => (
            <article
              key={source.key}
              className={`credit-order-panel__item${dragIndex === index ? ' is-dragging' : ''}`}
              draggable={!preview && !saving}
              onDragStart={() => setDragIndex(index)}
              onDragOver={event => event.preventDefault()}
              onDrop={() => handlePersonalDrop(index)}
              onDragEnd={() => setDragIndex(null)}
            >
              <span className="credit-order-panel__grip" aria-hidden="true"><GripVertical size={15} /></span>
              <div className="credit-order-panel__item-main">
                <strong>{source.title || source.key}</strong>
                <span>{formatExpiry(source.expire_at, english)}</span>
              </div>
              <b>{Number(source.credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</b>
              <div className="credit-order-panel__item-actions">
                <button type="button" disabled={preview || saving || index === 0} onClick={() => reorderPersonal(index, index - 1)} aria-label={english ? 'Move up' : '上移'}><ChevronUp size={13} /></button>
                <button type="button" disabled={preview || saving || index === orderedPersonalSources.length - 1} onClick={() => reorderPersonal(index, index + 1)} aria-label={english ? 'Move down' : '下移'}><ChevronDown size={13} /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      {feedback.message && (
        <div className={`credit-order-panel__feedback is-${feedback.type}`} role="status">
          {feedback.type === 'error' ? null : <Save size={13} />}
          {feedback.message}
        </div>
      )}
    </>
  );

  const renderTeamTab = () => (
    <>
      {teamLoading ? (
        <div className="credit-order-panel__state">{english ? 'Loading team order…' : '正在读取团队积分顺序…'}</div>
      ) : teamError ? (
        <div className="credit-order-panel__state is-error">
          <strong>{teamError}</strong>
          <button type="button" onClick={loadTeam} disabled={preview}>
            <RefreshCw size={13} />
            {english ? 'Retry' : '重新加载'}
          </button>
        </div>
      ) : (
        <>
          <div className="credit-order-panel__team-summary">
            <strong>{teamData?.team?.team_name || data?.team?.team_name || (english ? 'Current team' : '当前团队')}</strong>
            <span>{Number(teamData?.team_available_credits ?? data?.team_available_credits ?? 0).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</span>
          </div>

          <div className="credit-order-panel__notice">
            {english
              ? 'This order only affects team credit buckets. Team and personal credits are never mixed in one charge.'
              : '这里只调整团队积分内部批次顺序；团队和个人积分不会在同一次消费中混扣。'}
          </div>

          {orderedTeamSources.length === 0 ? (
            <div className="credit-order-panel__state">{english ? 'No team credit batches available.' : '暂无可排序的团队积分批次。'}</div>
          ) : (
            <div className="credit-order-panel__list">
              {orderedTeamSources.map((source, index) => (
                <article
                  key={source.key}
                  className={`credit-order-panel__item${teamDragIndex === index ? ' is-dragging' : ''}`}
                  draggable={!preview && !teamSaving}
                  onDragStart={() => setTeamDragIndex(index)}
                  onDragOver={event => event.preventDefault()}
                  onDrop={() => handleTeamDrop(index)}
                  onDragEnd={() => setTeamDragIndex(null)}
                >
                  <span className="credit-order-panel__grip" aria-hidden="true"><GripVertical size={15} /></span>
                  <div className="credit-order-panel__item-main">
                    <strong>{source.title || source.key}</strong>
                    <span>{formatExpiry(source.expire_at, english)}</span>
                  </div>
                  <b>{Number(source.credits || 0).toLocaleString(english ? 'en-US' : 'zh-CN')} {english ? 'credits' : '点'}</b>
                  <div className="credit-order-panel__item-actions">
                    <button type="button" disabled={preview || teamSaving || index === 0} onClick={() => reorderTeam(index, index - 1)} aria-label={english ? 'Move up' : '上移'}><ChevronUp size={13} /></button>
                    <button type="button" disabled={preview || teamSaving || index === orderedTeamSources.length - 1} onClick={() => reorderTeam(index, index + 1)} aria-label={english ? 'Move down' : '下移'}><ChevronDown size={13} /></button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {teamFeedback.message && (
            <div className={`credit-order-panel__feedback is-${teamFeedback.type}`} role="status">
              {teamFeedback.type === 'error' ? null : <Save size={13} />}
              {teamFeedback.message}
            </div>
          )}
        </>
      )}
    </>
  );

  return (
    <section className="credit-order-panel">
      <header className="credit-order-panel__header">
        <div>
          <span className="account-eyebrow">CREDIT ORDER</span>
          <h2>{english ? 'Credit Consumption Order' : '积分消耗顺序'}</h2>
          <p>{english ? 'Choose the preferred wallet and reorder credit batches.' : '选择优先钱包，并调整积分批次顺序。'}</p>
        </div>
        <span className={`account-status ${error || teamError ? 'account-status--warm' : 'account-status--ready'}`}>
          {loading ? (english ? 'Loading' : '正在读取') : error ? ('暂不可用') : saving || teamSaving ? (english ? 'Saving' : '正在保存') : (english ? 'Synced' : '已同步')}
        </span>
      </header>

      {loading ? (
        <div className="credit-order-panel__state">{english ? 'Loading credit order…' : '正在读取积分消耗顺序…'}</div>
      ) : error ? (
        <div className="credit-order-panel__state is-error">
          <strong>{error}</strong>
          <button type="button" onClick={loadPersonal} disabled={preview}>
            <RefreshCw size={13} />
            {english ? 'Retry' : '重新加载'}
          </button>
        </div>
      ) : (
        <>
          <div className="credit-order-panel__tabs" role="tablist" aria-label={english ? 'Credit order scope' : '积分顺序范围'}>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'personal'}
              className={activeTab === 'personal' ? 'is-active' : ''}
              onClick={() => setActiveTab('personal')}
            >
              {english ? 'Personal' : '个人积分'}
            </button>
            {teamCanManage && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'team'}
                className={activeTab === 'team' ? 'is-active' : ''}
                onClick={() => setActiveTab('team')}
              >
                {english ? 'Team' : '团队积分'}
              </button>
            )}
          </div>

          {data?.team?.team_name && (
            <div className="credit-order-panel__team-context">
              {english ? 'Current team' : '当前团队'}：{data.team.team_name}
            </div>
          )}

          {renderWalletCards()}
          {activeTab === 'personal' ? renderPersonalTab() : renderTeamTab()}
        </>
      )}
    </section>
  );
}
